import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PanResponder,
  Platform,
  type GestureResponderEvent,
} from "react-native";

import type { BoardViewport } from "./useBoardViewport";
import {
  isDoubleTap,
  panBy,
  passedDrag,
  pinchView,
  wheelFactor,
  zoomAbout,
  type Tap,
  type View,
} from "./viewport";

type Pt = { x: number; y: number };

/**
 * Wheel/trackpad zoom about the cursor, drag to pan, pinch to zoom and
 * double-click/double-tap to re-fit. Web listens on the board's DOM node
 * (wheel is non-passive so the page never scrolls under it; a press that
 * moved past the drag threshold swallows its click, so a drag never makes a
 * move). Native uses a PanResponder that only claims a touch once it moves
 * past the threshold or a second finger lands, so node taps still register.
 */
export function useViewportGestures(vp: BoardViewport, enabled: boolean) {
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const get = useCallback(() => vpRef.current, []);
  // A callback ref: the board's View mounts only once it has been measured.
  const [node, setNode] = useState<unknown>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || !enabled) return undefined;
    const el = node as HTMLElement | null;
    if (!el || typeof el.addEventListener !== "function") return undefined;
    return attachWeb(el, get);
  }, [node, enabled, get]);

  return { ref: setNode, handlers: useNativeHandlers(get, enabled) };
}

function attachWeb(el: HTMLElement, vp: () => BoardViewport): () => void {
  const pts = new Map<number, Pt>();
  let start: Pt = { x: 0, y: 0 };
  let pinch0: [Pt, Pt] | null = null;
  let base: View = vp().getView();
  let dragging = false;
  let swallowClick = false;
  let lastTap: Tap | null = null;

  const local = (e: { clientX: number; clientY: number }): Pt => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const rebase = () => {
    base = vp().getView();
    const [a, b] = [...pts.values()];
    pinch0 = b ? [a, b] : null;
    if (a && !b) start = a;
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const p = local(e);
    const f = wheelFactor(e.deltaY, e.deltaMode, e.ctrlKey);
    vp().update((v) => zoomAbout(v, f, p.x, p.y));
  };
  const onDown = (e: PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (pts.size === 0) dragging = false;
    pts.set(e.pointerId, local(e));
    if (pts.size >= 2) dragging = true;
    rebase();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  const onMove = (e: PointerEvent) => {
    if (!pts.has(e.pointerId)) return;
    const p = local(e);
    pts.set(e.pointerId, p);
    if (pinch0 && pts.size >= 2) {
      const [a, b] = [...pts.values()];
      const [a0, b0] = pinch0;
      vp().update(() => pinchView(base, a0, b0, a, b));
      return;
    }
    const dx = p.x - start.x;
    const dy = p.y - start.y;
    if (!dragging && passedDrag(dx, dy)) dragging = true;
    if (dragging) vp().update(() => panBy(base, dx, dy));
  };
  const onUp = (e: PointerEvent) => {
    if (!pts.has(e.pointerId)) return;
    const p = local(e);
    pts.delete(e.pointerId);
    if (pts.size > 0) {
      rebase();
      return;
    }
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    if (dragging) {
      swallowClick = true;
      setTimeout(() => (swallowClick = false), 0);
      return;
    }
    if (e.pointerType !== "mouse") {
      const tap = { t: e.timeStamp, x: p.x, y: p.y };
      if (isDoubleTap(lastTap, tap)) {
        lastTap = null;
        vp().refitNow();
      } else lastTap = tap;
    }
  };
  const onClick = (e: MouseEvent) => {
    if (!swallowClick) return;
    swallowClick = false;
    e.stopPropagation();
    e.preventDefault();
  };
  const onDblClick = () => vp().refitNow();

  el.addEventListener("wheel", onWheel, { passive: false });
  el.addEventListener("pointerdown", onDown);
  el.addEventListener("click", onClick, true);
  el.addEventListener("dblclick", onDblClick);
  return () => {
    el.removeEventListener("wheel", onWheel);
    el.removeEventListener("pointerdown", onDown);
    el.removeEventListener("click", onClick, true);
    el.removeEventListener("dblclick", onDblClick);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
  };
}

function useNativeHandlers(vp: () => BoardViewport, enabled: boolean) {
  return useMemo(() => {
    if (Platform.OS === "web" || !enabled) return {};
    let base: View = vp().getView();
    let pan0: Pt = { x: 0, y: 0 };
    let pinch0: [Pt, Pt] | null = null;
    let moved = false;
    let lastTap: Tap | null = null;
    const touchPts = (e: GestureResponderEvent): Pt[] =>
      e.nativeEvent.touches.map((t) => ({ x: t.pageX, y: t.pageY }));

    const responder = PanResponder.create({
      onMoveShouldSetPanResponderCapture: (_e, g) =>
        g.numberActiveTouches >= 2 || passedDrag(g.dx, g.dy),
      onPanResponderGrant: () => {
        moved = true;
        base = vp().getView();
        pan0 = { x: 0, y: 0 };
        pinch0 = null;
      },
      onPanResponderMove: (e, g) => {
        const t = touchPts(e);
        if (t.length >= 2) {
          if (!pinch0) {
            base = vp().getView();
            pinch0 = [t[0], t[1]];
          }
          const [a0, b0] = pinch0;
          vp().update(() => pinchView(base, a0, b0, t[0], t[1]));
          return;
        }
        if (pinch0) {
          pinch0 = null;
          base = vp().getView();
          pan0 = { x: g.dx, y: g.dy };
        }
        vp().update(() => panBy(base, g.dx - pan0.x, g.dy - pan0.y));
      },
      onPanResponderTerminationRequest: () => false,
    });

    return {
      ...responder.panHandlers,
      onTouchStart: (e: GestureResponderEvent) => {
        if (e.nativeEvent.touches.length <= 1) moved = false;
      },
      onTouchEnd: (e: GestureResponderEvent) => {
        if (moved || e.nativeEvent.touches.length > 0) return;
        const n = e.nativeEvent;
        const tap = { t: n.timestamp, x: n.pageX, y: n.pageY };
        if (isDoubleTap(lastTap, tap)) {
          lastTap = null;
          vp().refitNow();
        } else lastTap = tap;
      },
    };
  }, [vp, enabled]);
}
